const PUBLICATION_TIMEZONE = 'Europe/Madrid';

function getTimeZoneOffsetForZone(date, timeZone) {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
    return 0;
  }

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  let partsArray;
  try {
    partsArray = formatter.formatToParts(date);
  } catch (error) {
    return 0;
  }

  const parts = partsArray.reduce((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});

  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );

  return (asUTC - date.getTime()) / 60000;
}

function getPublicationDateParts(publishedAt) {
  if (!publishedAt) {
    return { publicationDate: '', publicationTime: '' };
  }

  const instant = new Date(publishedAt);
  if (Number.isNaN(instant.getTime())) {
    return { publicationDate: '', publicationTime: '' };
  }

  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
    return { publicationDate: '', publicationTime: '' };
  }

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: PUBLICATION_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  let partsArray;
  try {
    partsArray = formatter.formatToParts(instant);
  } catch (error) {
    return { publicationDate: '', publicationTime: '' };
  }

  const parts = partsArray.reduce((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});

  const date = parts.year && parts.month && parts.day
    ? `${parts.year}-${parts.month}-${parts.day}`
    : '';
  const time = parts.hour && parts.minute
    ? `${parts.hour}:${parts.minute}`
    : '';

  return {
    publicationDate: date,
    publicationTime: time
  };
}

function getAuctionEndParts(endsAt) {
  if (!endsAt) {
    return { auctionEndsDate: '', auctionEndsTime: '' };
  }

  const instant = new Date(endsAt);
  if (Number.isNaN(instant.getTime())) {
    return { auctionEndsDate: '', auctionEndsTime: '' };
  }

  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
    return { auctionEndsDate: '', auctionEndsTime: '' };
  }

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: PUBLICATION_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  let partsArray;
  try {
    partsArray = formatter.formatToParts(instant);
  } catch (error) {
    return { auctionEndsDate: '', auctionEndsTime: '' };
  }

  const parts = partsArray.reduce((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});

  const date = parts.year && parts.month && parts.day
    ? `${parts.year}-${parts.month}-${parts.day}`
    : '';
  const time = parts.hour && parts.minute
    ? `${parts.hour}:${parts.minute}`
    : '';

  return {
    auctionEndsDate: date,
    auctionEndsTime: time
  };
}

function hasValidStartingPriceValue(artworkAbout = {}) {
  if (!artworkAbout || typeof artworkAbout !== "object") {
    return false;
  }
  if (!Object.prototype.hasOwnProperty.call(artworkAbout, "price_starting_at")) {
    return false;
  }
  const value = artworkAbout.price_starting_at;
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === "number") {
    return Number.isFinite(value);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") {
      return false;
    }
    const numeric = Number(trimmed);
    return Number.isFinite(numeric);
  }
  return false;
}

function normalizeAuctionPricing(artworkAbout = {}, state) {
  const username = state?.user_session?.user_username?.toLowerCase?.();
  const isAuctionUploader = username === 'tgallery';

  const normalizedBase = {
    artwork_theme: Array.isArray(artworkAbout?.artwork_theme) ? artworkAbout.artwork_theme : [],
    artwork_feeling: Array.isArray(artworkAbout?.artwork_feeling) ? artworkAbout.artwork_feeling : [],
    artwork_color: Array.isArray(artworkAbout?.artwork_color) ? artworkAbout.artwork_color : [],
    artwork_size: artworkAbout?.artwork_size ?? {
      unit: "cm",
      height: "",
      length: "",
    },
    ...artworkAbout,
  };

  const draftPrice = artworkAbout.price_starting_at ?? artworkAbout.artwork_price ?? '';

  if (isAuctionUploader) {
    return {
      ...normalizedBase,
      artwork_is_auction: true,
      price_starting_at: draftPrice,
      // Explicitly clear the standard artwork price when the curated auction
      // account is preparing a listing. The backend will populate the auction
      // metadata using this value.
      artwork_price: '',
    };
  }

  const normalized = {
    ...normalizedBase,
  };

  if (!hasValidStartingPriceValue(normalized) && Object.prototype.hasOwnProperty.call(normalized, "price_starting_at")) {
    delete normalized.price_starting_at;
  }

  return normalized;
}

function resolveArtworkArtist(artworkAbout = {}) {
  const artistField = artworkAbout?.artwork_artist;
  const galleryArtist = artworkAbout?.artwork_gallery_artist;

  const artistHasDisplayName =
    artistField &&
    typeof artistField === 'object' &&
    (artistField.artist_name || artistField.user_name);

  if (artistHasDisplayName) {
    return artistField;
  }

  if (galleryArtist && typeof galleryArtist === 'object') {
    const mergedArtist = { ...galleryArtist };
    if (!mergedArtist._id) {
      if (typeof artistField === 'string') mergedArtist._id = artistField;
      else if (artistField && typeof artistField === 'object' && artistField._id) mergedArtist._id = artistField._id;
    }
    return mergedArtist;
  }

  return artistField ?? null;
}

function normalizeArtworkOfferSettings(artworkAbout = {}) {
  const raw = artworkAbout?.artwork_offer_settings;
  const mode = ['none', 'custom'].includes(String(raw?.mode ?? '').trim().toLowerCase())
    ? String(raw.mode).trim().toLowerCase()
    : 'default';
  const parsedMax =
    raw?.max_discount_pct === null || raw?.max_discount_pct === undefined || raw?.max_discount_pct === ''
      ? null
      : Number(raw.max_discount_pct);

  return {
    mode,
    max_discount_pct:
      mode === 'custom' && Number.isFinite(parsedMax)
        ? Math.max(0, Math.min(30, parsedMax))
        : null,
  };
}

function initArtworkFormState(artworkData, editMode, state) {
  const scheduleSource = artworkData?.draft_container ?? artworkData ?? null;
  const publishedAt = scheduleSource?.publishedAt ?? null;
  const { publicationDate, publicationTime } = getPublicationDateParts(publishedAt);
  const publicationTimezone = publishedAt ? PUBLICATION_TIMEZONE : null;
  const publicationOffsetMinutes = publishedAt
    ? getTimeZoneOffsetForZone(new Date(publishedAt), PUBLICATION_TIMEZONE)
    : null;
  const auctionSource = scheduleSource?.artwork_auction ?? artworkData?.artwork_auction ?? null;
  const endsAt = auctionSource?.ends_at ?? null;
  const { auctionEndsDate, auctionEndsTime } = getAuctionEndParts(endsAt);
  const computedStatus = scheduleSource?.draft_status
    ?? scheduleSource?.status
    ?? scheduleSource?.artwork_status
    ?? null;

  if (editMode && artworkData) {
    const normalizedAbout = normalizeAuctionPricing(artworkData.artwork_about, state);
    return {
      _id: artworkData._id,
      artwork_about: {
        ...normalizedAbout,
        artwork_artist: resolveArtworkArtist(artworkData.artwork_about),
        artwork_offer_settings: normalizeArtworkOfferSettings(artworkData.artwork_about),
      },
      artwork_media: artworkData.artwork_media,
      artwork_shipping: artworkData.artwork_shipping,
      artwork_limited_editions: artworkData.artwork_limited_editions,
      artwork_auction: {
        ...(artworkData.artwork_auction ?? {}),
        ends_at: endsAt,
      },
      publishedAt,
      publicationDate,
      publicationTime,
      publicationTimezone,
      publicationOffsetMinutes,
      auctionEndsDate,
      auctionEndsTime,
      draft_status: computedStatus,
      initialDraftStatus: computedStatus,
    };
  }

  if (artworkData) {
    const draftAbout = artworkData.draft_container.artwork_about;
    return {
      _id: artworkData._id,
      lastCompletedStep: artworkData.draft_container.lastCompletedStep,
      artwork_about: {
        ...normalizeAuctionPricing(draftAbout, state),
        artwork_artist: resolveArtworkArtist(draftAbout),
        artwork_offer_settings: normalizeArtworkOfferSettings(draftAbout),
      },
      artwork_media: artworkData.draft_container.artwork_media,
      artwork_shipping: artworkData.draft_container.artwork_shipping,
      artwork_auction: {
        ...(artworkData.draft_container.artwork_auction ?? {}),
        ends_at: endsAt,
      },
      publishedAt,
      publicationDate,
      publicationTime,
      publicationTimezone,
      publicationOffsetMinutes,
      auctionEndsDate,
      auctionEndsTime,
      draft_status: computedStatus ?? 'incomplete',
      initialDraftStatus: computedStatus ?? 'incomplete',
    };
  }

  return {
    _id: null,
    lastCompletedStep: 0,
    artwork_about: normalizeAuctionPricing({
      artwork_type: null,
      artwork_artist: null,
      artwork_title: "",
      artwork_description: "",
      artwork_medium: "",
      artwork_year: "",
      artwork_size: {
        unit: "cm",
        height: "",
        length: "",
      },
      artwork_theme: [],
      artwork_feeling: [],
      artwork_color: [],
      artwork_currency: "",
      artwork_price: "",
      artwork_limited_edition: false,
      artwork_includes_certificate: false,
      artwork_includes_frame: false,
      artwork_offer_settings: {
        mode: 'default',
        max_discount_pct: null,
      },
    }, state),
    artwork_auction: {
      ends_at: null,
    },
    artwork_media: {
      artwork_main_picture: null,
      artwork_secondary_pictures: [],
      artwork_audio: null,
    },
    artwork_shipping: {
      artwork_shipping_own: {
        payer: "The collector",
        currency: "",
        price: "",
        location: state?.user_session?.gallery_address?.country ?? "",
      },
      artwork_shipping_rest: {
        payer: "The collector",
        currency: "",
        price: "",
      },
      artwork_shipping_exceptions: [],
    },
    publishedAt: null,
    publicationDate: '',
    publicationTime: '',
    publicationTimezone: null,
    publicationOffsetMinutes: null,
    auctionEndsDate: '',
    auctionEndsTime: '',
    draft_status: 'incomplete',
    initialDraftStatus: 'incomplete',
  };
}
export function initPostFormState(postData) {

  if (postData) {
    const formData = postData.post_container;
    const audience = String(postData.audience ?? formData?.audience ?? 'public')
      .trim()
      .toLowerCase() === 'vip_only'
      ? 'vip_only'
      : 'public';

    const publishedAt = postData.publishedAt ?? null;
    const { publicationDate, publicationTime } = getPublicationDateParts(publishedAt);

    return {
      _id: postData._id,
      post_cover: formData.post_cover,
      post_title: formData.post_title,
      post_description: formData.post_description,
      post_structure: formData.post_structure,
      post_texts: formData.post_texts,
      post_pictures: formData.post_pictures,
      post_tags: formData.post_tags,
      post_polls: formData.post_polls,
      post_quizzes: formData.post_quizzes,
      post_artworks: formData.post_artworks,
      post_videos: formData.post_videos || [],
      audience,
      publishedAt,
      publicationDate,
      publicationTime,
      // Only set these when a schedule exists
      publicationTimezone: publishedAt ? PUBLICATION_TIMEZONE : null,
      publicationOffsetMinutes: publishedAt
        ? getTimeZoneOffsetForZone(new Date(publishedAt), PUBLICATION_TIMEZONE)
        : null,
    };
  } else {
    const publishedAt = null;
    const { publicationDate, publicationTime } = getPublicationDateParts(publishedAt);

    return {
      _id: null,
      post_cover: null,
      post_title: "",
      post_description: "",
      post_pictures: [],
      post_structure: [{ type: 'post_texts', id: 1 }],
      post_texts: [{ textId: 1, content: '<div><br></div>' }],
      post_tags: [],
      post_polls: [],
      post_quizzes: [],
      post_artworks: [],
      post_videos: [],
      audience: 'public',
      publishedAt,
      publicationDate,
      publicationTime,
      // Unscheduled drafts should *not* carry schedule metadata
      publicationTimezone: null,
      publicationOffsetMinutes: null,
    };
  }
}

function isPostMediaUploading(formState) {
  return formState.post_cover?.loading === true
    || (formState.post_pictures || []).some(group => (
      group.pictures?.some(picture => picture.status === 'uploading')
    ))
    || (formState.post_videos || []).some(video => (
      video.status === 'uploading'
    ));
}

function isPostVideoProcessing(formState) {
  return (formState.post_videos || []).some(video => (
    video.status === 'uploading'
    || video.status === 'processing'
    || (video.status === 'ready' && video.isFullyProcessed === false)
  ));
}

function initShippingAddressForm(addressData) {
  const baseAddress = {
    address_name: "",
    address_surname: "",
    address_street: "",
    address_city: "",
    address_zip_code: "",
    address_region: "",
    address_country: "",
    address_phone: {
      address_phone_prefix: "",
      address_phone_number: "",
    },
  };

  if (!addressData) {
    return baseAddress;
  }

  const normalizedPhone = {
    ...baseAddress.address_phone,
    ...(addressData.address_phone ?? {}),
  };

  return {
    ...baseAddress,
    ...addressData,
    address_phone: normalizedPhone,
  };
}

function initArtistForm(artistData) {
  if (artistData) {
    return {
      ...artistData,
      // Already there:
      isArtistDeceased: Boolean(artistData.artist_death),
      // New: track “unknown death year”
      isDeathUnknown: Boolean(artistData.isDeathUnknown),
      // Existing: track “unknown birth year”
      isBirthUnknown: Boolean(artistData.isBirthUnknown),
    };
  } else {
    return {
      artist_name: "",
      artist_email: "",
      artist_nationality: "",
      artist_birth: "",
      isArtistDeceased: false,
      artist_death: "",
      // New: default to “unknown death” false
      isDeathUnknown: false,
      // Existing: default to “known birth”
      isBirthUnknown: false,
    };
  }
}

/**
 * Walks through a contentEditable container’s children
 * and builds post_structure + post_texts arrays.
 */
export function buildArticleStructureFromDOM(editable) {
  const post_structure = [];
  const post_texts = [];
  let textId = 1;

  Array.from(editable.childNodes).forEach(node => {
    // 1) Atomic widgets (poll, quiz, picture, artwork)
    if (
      node.nodeType === Node.ELEMENT_NODE &&
      node.dataset.atomic === 'true'
    ) {
      const [kind, idx] = node.id.split('-');
      const map = {
        poll: 'post_polls',
        quiz: 'post_quizzes',
        picture: 'post_pictures',
        artwork: 'post_artworks',
      };
      if (map[kind]) {
        post_structure.push({ type: map[kind], id: Number(idx) });
      }
      return;
    }

    // 2) Plain text or HTML nodes
    let content;
    if (node.nodeType === Node.TEXT_NODE) {
      content = node.textContent.trim();
      if (!content) return;
    } else {
      content = node.outerHTML;
    }

    post_texts.push({ textId, content });
    post_structure.push({ type: 'post_texts', id: textId });
    textId += 1;
  });

  return { post_structure, post_texts };
}

const FormUtils = {
  initArtworkFormState,
  initPostFormState,
  isPostMediaUploading,
  isPostVideoProcessing,
  initShippingAddressForm,
  initArtistForm,
  buildArticleStructureFromDOM,
};

export default FormUtils;
